// 现代token存储方案
export function setToken(token, remember = false) {
  if (remember) {
    localStorage.setItem("token", token);
    localStorage.setItem("tokenExpiry", "persistent"); // 持久化标识
    sessionStorage.removeItem("token");
  } else {
    // 未勾选记住我时，也存储在localStorage中，但设置过期时间为24小时
    localStorage.setItem("token", token);
    const expiryTime = new Date().getTime() + 24 * 60 * 60 * 1000; // 24小时后过期
    localStorage.setItem("tokenExpiry", expiryTime.toString());
    sessionStorage.removeItem("token");
  }
}

export function getToken() {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) {
    const expiry = localStorage.getItem("tokenExpiry");
    // 检查是否过期
    if (expiry && expiry !== "persistent") {
      const currentTime = new Date().getTime();
      if (currentTime > parseInt(expiry)) {
        // 已过期，清除token
        removeToken();
        return null;
      }
    }
    return token;
  }
  return null;
}

export function removeToken() {
  sessionStorage.removeItem("token");
  localStorage.removeItem("token");
  localStorage.removeItem("tokenExpiry");
}

// 清除所有用户相关数据
export function clearUserData() {
  // 清除token
  removeToken();

  // 清除用户信息
  localStorage.removeItem("account");
  localStorage.removeItem("avatar");
  localStorage.removeItem("userName");
  localStorage.removeItem("vip");
  localStorage.removeItem("vipName");
  localStorage.removeItem("vipDate");
  localStorage.removeItem("uploadimage");
  localStorage.removeItem("userInfo");
  localStorage.removeItem("accountExpiry");
  localStorage.removeItem("avatarExpiry");
  localStorage.removeItem("userNameExpiry");
  localStorage.removeItem("vipExpiry");
  localStorage.removeItem("vipNameExpiry");
  localStorage.removeItem("vipDateExpiry");
  localStorage.removeItem("uploadimageExpiry");

  // 清除管理员权限信息（包括JWT token）
  localStorage.removeItem("admin_token");
  localStorage.removeItem("admin_token_expiry");
  localStorage.removeItem("admin_token_jwt");
  localStorage.removeItem("admin_token_jwt_expiry");
  localStorage.removeItem("admin_user_info");
  localStorage.removeItem("admin_permissions");
  localStorage.removeItem("admin_session");

  // 清除sessionStorage中的用户数据
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("account");
  sessionStorage.removeItem("avatar");
  sessionStorage.removeItem("userName");
  sessionStorage.removeItem("vip");
  sessionStorage.removeItem("vipName");
  sessionStorage.removeItem("vipDate");
  sessionStorage.removeItem("uploadimage");
  sessionStorage.removeItem("userInfo");
  
  // 清除sessionStorage中的admin相关数据
  sessionStorage.removeItem("admin_token");
  sessionStorage.removeItem("admin_token_jwt");
  sessionStorage.removeItem("admin_user_info");
  sessionStorage.removeItem("admin_permissions");
  sessionStorage.removeItem("admin_session");
  
  // 清除其他可能的缓存数据（如订单号等临时数据）
  // 注意：openid 通常保留，因为它是第三方登录标识，不是用户凭证
  // 如果需要清除 openid，可以在退出登录时单独调用 localStorage.removeItem("openid")
}

// 清除非持久化的用户数据（保留"记住我"的数据）
export function clearSessionData() {
  // 只清除sessionStorage中的数据
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("account");
  sessionStorage.removeItem("avatar");
  sessionStorage.removeItem("userName");
  sessionStorage.removeItem("vip");
  sessionStorage.removeItem("vipName");
  sessionStorage.removeItem("vipDate");
  sessionStorage.removeItem("uploadimage");
}

// 检查是否有"记住我"的登录状态
export function hasRememberedLogin() {
  return (
    localStorage.getItem("token") !== null &&
    localStorage.getItem("tokenExpiry") === "persistent"
  );
}

// 检查当前登录状态
export function isLoggedIn() {
  return getToken() !== null;
}

// 获取用户信息（优先从localStorage获取，支持"记住我"功能）
export function getUserInfo() {
  // 先检查token是否有效
  const token = getToken();
  if (!token) {
    // token无效，清除所有用户数据
    clearUserData();
    return {
      token: "",
      account: "",
      avatar: "",
      userName: "",
      vip: false,
      vipName: "",
      vipDate: "",
      uploadimage: "",
      // 管理员权限字段
      canFileManage: false,
      canUserManage: false,
      canSystemManage: false,
      role: "",
      permissionLevel: 0,
      userInfo: {}
    };
  }

  // 获取管理员权限信息
  let adminPermissions = {
    canFileManage: false,
    canUserManage: false,
    canSystemManage: false,
    role: "",
    permissionLevel: 0
  };

  // 检查是否有管理员登录状态
  const adminToken = localStorage.getItem('admin_token');
  if (adminToken) {
    // 检查管理员token是否过期
    const adminExpiry = localStorage.getItem('admin_token_expiry');
    if (adminExpiry) {
      const currentTime = new Date().getTime();
      if (currentTime <= parseInt(adminExpiry)) {
        // 管理员token有效，获取权限信息
        const storedPermissions = localStorage.getItem('admin_permissions');
        if (storedPermissions) {
          try {
            adminPermissions = JSON.parse(storedPermissions);
          } catch (error) {
            console.error('解析管理员权限信息失败:', error);
          }
        }
      }
    }
  }

  return {
    token: token,
    account: checkExpiry("account") || "",
    avatar: checkExpiry("avatar") || "",
    userName: checkExpiry("userName") || "",
    vip: checkExpiry("vip", true) || false, // 强制布尔，处理undefined
    vipName: checkExpiry("vipName") || "",
    vipDate: checkExpiry("vipDate") || "",
    uploadimage: checkExpiry("uploadimage") || "",
    // 管理员权限字段
    canFileManage: adminPermissions.canFileManage || false,
    canUserManage: adminPermissions.canUserManage || false,
    canSystemManage: adminPermissions.canSystemManage || false,
    role: adminPermissions.role || "",
    permissionLevel: adminPermissions.permissionLevel || 0,
    userInfo: adminPermissions.userInfo || {}
  };
}

// 检查数据是否过期的辅助函数
function checkExpiry(key, toBool = false) {
  const value = localStorage.getItem(key);
  const expiry = localStorage.getItem(`${key}Expiry`);
  if (value && expiry) {
    if (expiry === "persistent") {
      if (toBool) return value === "true";
      return value;
    }
    const currentTime = new Date().getTime();
    if (currentTime <= parseInt(expiry)) {
      if (toBool) return value === "true";
      return value;
    } else {
      // 已过期，清除该数据
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}Expiry`);
    }
  }
  return toBool ? false : null;
}
